import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { IRedlineableField, JSONType } from "work/proposals/redlining/field-redlines/field-redline";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import { ProposalFieldJSON } from "./_schema/proposal-field-schema";
import EntityMember from "_shared/entity-members/entity-member";
import Percent from "_shared/percentages/percent";
import WorkDocument from "work/work-agreements/contracts/work-document";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";

export enum ProposalFieldCategory {
  Details = "Details",
  Team = "Team",
  FeeSchedule = "Fee Schedule",
  Conflicts = "Conflicts",
  Policies = "Policies",
  Discount = "Discount",
}

export enum ProposalFieldName {
  General = "general",
  Client = "client",
  Name = "name",
  Description = "description",
  Negotiable = "negotiable",
  ResponseDueBy = "responseDueBy",
  StartDate = "startDate",
  EndDate = "endDate",
  TeamLeader = "teamLeader",
  Team = "team",
  FeeSchedule = "feeSchedule",
  WaiveConflictsCheck = "waiveConflictsCheck",
  TeamRestriction = "teamRestriction",
  Conflicts = "conflicts",
  Policies = "policies",
  ClientPolicies = "clientPolicies",
  VendorPolicies = "vendorPolicies",
  Discount = "discount",
}

export enum HumanReadableProposalFieldName {
  client = "Client",
  name = "Name",
  description = "Description",
  negotiable = "Negotiable",
  responseDueBy = "Response Due By",
  startDate = "Start Date",
  endDate = "End Date",
  teamLeader = "Team Leader",
  team = "Team",
  feeSchedule = "Fee Schedule",
  waiveConflictsCheck = "Waive Conflicts Check",
  teamRestriction = "Team Restriction",
  conflicts = "Conflicts",
  policies = "Policies",
  clientPolicies = "Client Policies",
  vendorPolicies = "Vendor Policies",
  discount = "Discount",
  general = "General",
}

export default class ProposalField {
  public static readonly General: ProposalField = new ProposalField({
    name: ProposalFieldName.General,
    category: ProposalFieldCategory.Details
  });
  public static readonly Client: ProposalField = new ProposalField({
    name: ProposalFieldName.Client,
    category: ProposalFieldCategory.Details
  });
  public static readonly Name: ProposalField = new ProposalField({
    name: ProposalFieldName.Name,
    category: ProposalFieldCategory.Details,
    fromJSON: ProjectName.fromJSON
  });
  public static readonly Description: ProposalField = new ProposalField({
    name: ProposalFieldName.Description,
    category: ProposalFieldCategory.Details,
    fromJSON: ProjectDescription.fromJSON
  });
  public static readonly Negotiable: ProposalField = new ProposalField({
    name: ProposalFieldName.Negotiable,
    category: ProposalFieldCategory.Details,
    fromJSON: AHBoolean.fromJSON
  });
  public static readonly ResponseDueBy: ProposalField = new ProposalField({
    name: ProposalFieldName.ResponseDueBy,
    category: ProposalFieldCategory.Details,
    fromJSON: AHDate.fromUnknownJSON
  });
  public static readonly StartDate: ProposalField = new ProposalField({
    name: ProposalFieldName.StartDate,
    category: ProposalFieldCategory.Details,
    fromJSON: AHDate.fromUnknownJSON
  });
  public static readonly EndDate: ProposalField = new ProposalField({
    name: ProposalFieldName.EndDate,
    category: ProposalFieldCategory.Details,
    fromJSON: AHDate.fromUnknownJSON
  });
  public static readonly Team: ProposalField = new ProposalField({
    name: ProposalFieldName.Team,
    category: ProposalFieldCategory.Team,
    fromJSON: EntityMember.fromUnknownJSON
  });
  public static readonly TeamRestriction: ProposalField = new ProposalField({
    name: ProposalFieldName.TeamRestriction,
    category: ProposalFieldCategory.Team,
    fromJSON: AHBoolean.fromJSON,
    commentField: ProposalField.Team
  });
  public static readonly TeamLeader: ProposalField = new ProposalField({
    name: ProposalFieldName.TeamLeader,
    category: ProposalFieldCategory.Team,
    fromJSON: EntityMember.fromUnknownJSON,
    commentField: ProposalField.Team
  });
  public static readonly FeeSchedule: ProposalField = new ProposalField({
    name: ProposalFieldName.FeeSchedule,
    category: ProposalFieldCategory.FeeSchedule,
    fromJSON: FeeScheduleCategory.fromUnknownJSON
  });
  public static readonly Conflicts: ProposalField = new ProposalField({
    name: ProposalFieldName.Conflicts,
    category: ProposalFieldCategory.Conflicts,
    fromJSON: WorkDocument.fromUnknownJSON
  });
  public static readonly WaiveConflictsCheck: ProposalField = new ProposalField({
    name: ProposalFieldName.WaiveConflictsCheck,
    category: ProposalFieldCategory.Conflicts,
    fromJSON: AHBoolean.fromJSON,
    commentField: ProposalField.Conflicts
  });
  public static readonly Policies: ProposalField = new ProposalField({
    name: ProposalFieldName.Policies,
    category: ProposalFieldCategory.Policies,
    fromJSON: WorkDocument.fromUnknownJSON
  });
  public static readonly ClientPolicies: ProposalField = new ProposalField({
    name: ProposalFieldName.ClientPolicies,
    category: ProposalFieldCategory.Policies,
    fromJSON: WorkDocument.fromUnknownJSON
  });
  public static readonly VendorPolicies: ProposalField = new ProposalField({
    name: ProposalFieldName.VendorPolicies,
    category: ProposalFieldCategory.Policies,
    fromJSON: WorkDocument.fromUnknownJSON
  });
  public static readonly Discount: ProposalField = new ProposalField({
    name: ProposalFieldName.Discount,
    category: ProposalFieldCategory.Discount,
    fromJSON: Percent.fromUnknownJSON
  });

  public static fromJSON(json?: ProposalFieldJSON | "general" | null): ProposalField | null | undefined {
    if (isNullOrUndefined(json)) return json;
    if (json === "general") return ProposalField.General;

    return new ProposalField({
      name: json.name as ProposalFieldName,
      category: json.category as ProposalFieldCategory,
      id: isNullOrUndefined(json.id) ? undefined : Guid.fromJSON(json.id) ?? undefined,
      commentField: isNullOrUndefined(json.commentFieldOverride)
        ? undefined
        : (ProposalField.fromJSON(json.commentFieldOverride) ?? undefined),
    });
  }

  public static TeamMember(id: Guid): ProposalField {
    return new ProposalField({
      name: ProposalFieldName.Team,
      category: ProposalFieldCategory.Team,
      id,
      commentField: ProposalField.Team,
    });
  }

  public static FeeScheduleCategory(id: Guid): ProposalField {
    return new ProposalField({
      name: ProposalFieldName.FeeSchedule,
      category: ProposalFieldCategory.FeeSchedule,
      id,
      commentField: ProposalField.FeeSchedule,
    });
  }

  public static ConflictsDocument(id: Guid): ProposalField {
    return new ProposalField({
      name: ProposalFieldName.Conflicts,
      category: ProposalFieldCategory.Conflicts,
      id,
      commentField: ProposalField.Conflicts,
    });
  }

  public static ClientPolicy(id: Guid): ProposalField {
    return new ProposalField({
      name: ProposalFieldName.ClientPolicies,
      category: ProposalFieldCategory.Policies,
      id,
      commentField: ProposalField.ClientPolicies,
    });
  }

  public static VendorPolicy(id: Guid): ProposalField {
    return new ProposalField({
      name: ProposalFieldName.VendorPolicies,
      category: ProposalFieldCategory.Policies,
      id,
      commentField: ProposalField.VendorPolicies,
    });
  }

  public readonly id?: Guid;
  public readonly name: ProposalFieldName;
  public readonly category: ProposalFieldCategory;
  public readonly fromJSON: (json: JSONType) => IRedlineableField | null | undefined;

  public get cacheKey(): string {
    let keyBase = `${this.name}.${this.category}`;
    return this.id ? `${keyBase}.${this.id.value}` : keyBase;
  }
  public get commentField(): ProposalField {
    return this._commentFieldOverride ?? this;
  }

  public toJSON(): ProposalFieldJSON {
    return {
      name: this.name,
      category: this.category,
      id: this.id?.toJSON(),
      commentFieldOverride: this._commentFieldOverride?.toJSON(),
    };
  }

  public isEqualTo(field?: ProposalField | null): boolean {
    if (!field) return false;
    if (this.name !== field.name) return false;
    if (this.category !== field.category) return false;

    // If either has an id, both must and they must match
    if (this.id || field.id) {
      return !!this.id && !!field.id && this.id.isEqualTo(field.id);
    }
    return true;
  }

  public toString(): string {
    return this.name.toString();
  }

  public constructor(args: {
    name: ProposalFieldName;
    category: ProposalFieldCategory;
    id?: Guid;
    fromJSON?: (json: JSONType) => IRedlineableField | null | undefined;
    commentField?: ProposalField;
  }) {
    this.name = args.name;
    this.category = args.category;
    this.id = args.id;
    this.fromJSON = args.fromJSON ?? (() => undefined);
    this._commentFieldOverride = args.commentField;
  }

  protected readonly _commentFieldOverride?: ProposalField;
}

export type FreelyPatchableFields = {
  reviewers: ProposalReviewer[];
  teamTemplateIds: Guid[];
  feeScheduleTemplateIds: Guid[];
};
